'use client';

import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getShippingRegion } from '@util/firestore/cart';
import { updateOffer } from '@util/firestore/offers';
import { getPaymentMethods } from '@util/firestore/payments';
import {
  getProductById,
  getProductByIdNotClean,
} from '@util/firestore/products';
import { isDomestic } from '@util/firestore/shipengine';
import { getTax } from '@util/firestore/taxjar';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { capitalize } from 'lodash';
import { OfferDocument } from 'models/offer';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { useOffers } from '../useOffers';
import { CreditCard } from '@ui/CreditCard';
import OfferShippingOptions from './OfferShippingOptions';
import type { Rate } from '@util/firestore/shipengine';

const acceptOfferSchema = z.object({
  shipping_address: z.string().optional(),
  payment_method: z.string().optional(),
});

type AcceptOfferFormData = z.infer<typeof acceptOfferSchema>;

interface AcceptOfferContentProps {
  offer: OfferDocument;
  onClose: () => void;
  isSeller: boolean;
}

export default function AcceptOfferContent({
  offer,
  onClose,
  isSeller,
}: AcceptOfferContentProps) {
  if (isSeller) {
    return <AcceptOfferSellerContent offer={offer} onClose={onClose} />;
  }

  return <AcceptOfferBuyerContent offer={offer} onClose={onClose} />;
}

const AcceptOfferSellerContent = ({
  offer,
  onClose,
}: {
  offer: OfferDocument;
  onClose: () => void;
}) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { getNetEarnings, acceptThatOffer } = useOffers(offer.id);
  const { showErrorToast } = useToastContext();
  const { data: product } = useQuery({
    queryKey: ['product', offer.product_id],
    queryFn: () => getProductById(offer.product_id),
    enabled: !!offer.product_id,
  });

  return (
    <>
      <div className="my-8 flex flex-col items-center justify-center gap-y-8">
        <h3 className="w-[40rem] text-center text-[2rem] text-zinc-800">
          By accepting this offer, you agree to sell this item for{' '}
          <span className="font-bold text-black">
            {formatCurrency(offer.total)}
          </span>
        </h3>

        <div className="mt-4 flex w-full justify-center">
          <div className="flex items-center justify-center rounded-2xl bg-brand-lightest-gray px-4 py-2 text-[1.8rem] text-zinc-900">
            ({formatCurrency(offer.price)} +{' '}
            {formatCurrency(offer.shipping_cost)} Shipping +{' '}
            {formatCurrency(offer.tax)} Tax)
          </div>
        </div>

        <div className="flex flex-col items-center gap-y-4">
          <h3 className="w-[40rem] text-center text-[2rem] text-zinc-800">
            Your earnings for this order will be
          </h3>

          {product && (
            <h3 className="text-[3rem] font-bold text-black">
              {formatCurrency(getNetEarnings(offer))}
            </h3>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col items-center justify-center bg-brand-lightest-gray p-12">
        <span className="text-[1.8rem] text-zinc-700">
          Once accepted, offers are binding and cannot be cancelled.
        </span>

        <div className="mt-8 flex w-full items-center gap-x-8">
          <button
            type="button"
            className="flex w-full items-center justify-center rounded-[1.5rem] border border-brand-black bg-inherit p-4 text-[1.8rem] font-semibold text-brand-black"
            onClick={onClose}
            disabled={loadingSubmit}
          >
            Cancel
          </button>

          <button
            type="button"
            className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#1E7A1E] p-4 text-[1.8rem] font-semibold text-white disabled:bg-brand-gray disabled:text-black"
            onClick={async () => {
              setLoadingSubmit(true);
              try {
                await acceptThatOffer();
                onClose();
              } catch (e) {
                showErrorToast((e as Error).message);
                onClose();
              }
            }}
            disabled={loadingSubmit}
          >
            {loadingSubmit ? 'Accepting...' : 'Accept'}
          </button>
        </div>
      </div>
    </>
  );
};

const AcceptOfferBuyerContent = ({
  offer,
  onClose,
}: {
  offer: OfferDocument;
  onClose: () => void;
}) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [selectedRate, setSelectedRate] = useState<Rate | null>(null);
  const [ratesError, setRatesError] = useState<Error | null>(null);
  const [cardListCollapsed, setCardListCollapsed] = useState(false);
  const [calculatingTax, setCalculatingTax] = useState(false);
  const [taxError, setTaxError] = useState<Error | null>(null);
  const queryClient = useQueryClient();
  const { acceptThatOffer } = useOffers(offer.id);
  const { data: paymentMethodData, isLoading } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  });

  const { userDoc } = useAuth();

  const { data: product } = useQuery({
    queryKey: ['product-with-shipment', offer.product_id],
    queryFn: () => getProductByIdNotClean(offer.product_id),
    enabled: !!offer.product_id,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<AcceptOfferFormData>({
    resolver: zodResolver(acceptOfferSchema),
    defaultValues: {
      shipping_address: offer.address
        ? `${offer.address.address_line1} - ${offer.address.city_locality}, ${offer.address.state_province}, ${offer.address.postal_code}`
        : '',
      payment_method: offer.payment?.id || '',
    },
  });

  const form = watch();

  useEffect(() => {
    if (
      paymentMethodData &&
      paymentMethodData.length > 0 &&
      !offer.payment?.id
    ) {
      const defaultPaymentMethod =
        paymentMethodData[paymentMethodData.length - 1];
      setValue('payment_method', defaultPaymentMethod.id ?? '');
    }
  }, [offer.payment?.id, paymentMethodData, setValue]);

  useEffect(() => {
    if (selectedRate && selectedRate.rate_id !== offer.rate_id) {
      setCalculatingTax(true);
      getTax({
        product_id: offer.product_id,
        seller_id: offer.seller_id,
        to_address:
          offer.address ||
          userDoc?.addresses.find(
            (a) =>
              `${a.address_line1} - ${a.city_locality}, ${a.state_province}, ${a.postal_code}` ===
              form.shipping_address
          )!,
        total: offer.price,
        shipping: selectedRate.total_amount,
      })
        .then((tax) => {
          updateOffer(offer.id, {
            tax: tax.amount,
            rate_id: selectedRate.rate_id,
            shipping_cost: selectedRate.total_amount,
            total: offer.price + tax.amount + selectedRate.total_amount,
          }).then(() => {
            queryClient.invalidateQueries({
              queryKey: ['offer', offer.id],
            });
            setCalculatingTax(false);
          });
        })
        .catch((e) => {
          setTaxError(e as Error);
          logError(e);
          setCalculatingTax(false);
        });
    } else if (product?.is_flat_rate) {
      const address = userDoc?.addresses.find(
        (a) =>
          `${a.address_line1} - ${a.city_locality}, ${a.state_province}, ${a.postal_code}` ===
          form.shipping_address
      );
      if (address) {
        const region = getShippingRegion(address);
        const shipping_cost = product.shipping_costs?.find(
          (c) => c.code === region
        )?.cost;
        if (!shipping_cost) {
          return;
        }
        setCalculatingTax(true);
        getTax({
          product_id: offer.product_id,
          seller_id: offer.seller_id,
          to_address:
            offer.address ||
            userDoc?.addresses.find(
              (a) =>
                `${a.address_line1} - ${a.city_locality}, ${a.state_province}, ${a.postal_code}` ===
                form.shipping_address
            )!,
          total: offer.price,
          shipping: shipping_cost,
        }).then((tax) => {
          updateOffer(offer.id, {
            tax: tax.amount,
            shipping_cost,
            total: offer.price + tax.amount + shipping_cost,
          })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: ['offer', offer.id],
              });
              setCalculatingTax(false);
            })
            .catch((e) => {
              setTaxError(e as Error);
              logError(e);
              setCalculatingTax(false);
            });
        });
      }
    }
  }, [
    selectedRate,
    form.shipping_address,
    offer.rate_id,
    offer.address,
    offer.product_id,
    offer.seller_id,
    offer.price,
    offer.id,
    product?.is_flat_rate,
    product?.shipping_costs,
    userDoc?.addresses,
    queryClient,
  ]);

  const addressOptions =
    userDoc?.addresses?.map((address) => ({
      id: `${address.address_line1} - ${address.city_locality}, ${address.state_province}, ${address.postal_code}`,
      label: `${address.address_line1} - ${address.city_locality}, ${address.state_province}, ${address.postal_code}`,
      value: `${address.address_line1} - ${address.city_locality}, ${address.state_province}, ${address.postal_code}`,
    })) ?? [];

  const selectedPayment = paymentMethodData?.find(
    (method) =>
      method.id === form.payment_method || method.id === offer.payment?.id
  );

  const onFormSubmit = handleSubmit(async (data) => {
    if (loadingSubmit || calculatingTax) return;
    setLoadingSubmit(true);

    const selectedAddress = data.shipping_address
      ? userDoc?.addresses?.find(
          (a) =>
            `${a.address_line1} - ${a.city_locality}, ${a.state_province}, ${a.postal_code}` ===
            data.shipping_address
        )
      : offer.address;

    if (!selectedPayment) {
      setLoadingSubmit(false);
      setError('payment_method', {
        type: 'manual',
        message: 'Please select a payment method',
      });
      return;
    }

    if (!selectedAddress?.address_line1) {
      setLoadingSubmit(false);
      setError('shipping_address', {
        type: 'manual',
        message: 'Please select a shipping address',
      });
      return;
    }

    if (
      !product?.is_flat_rate &&
      !(selectedRate || offer.rate_id) &&
      !isDomestic(product?.shipment, selectedAddress)
    ) {
      setLoadingSubmit(false);
      setError('root', {
        type: 'manual',
        message: 'Please select a shipping rate',
      });
      return;
    }

    const updatedOffer = {
      payment: selectedPayment,
      address: selectedAddress,
    };
    try {
      await updateOffer(offer.id, updatedOffer);
      await acceptThatOffer();
      onClose();
    } catch (e) {
      logError(e);
      setLoadingSubmit(false);
    }
  });

  return (
    <form onSubmit={onFormSubmit}>
      <div className="my-8 flex flex-col items-center justify-center gap-y-8">
        <h2 className="text-center text-[2rem] font-semibold text-zinc-800">
          {product?.title}
        </h2>

        <h3 className="w-[40rem] text-center text-[2rem] text-zinc-800">
          By accepting this offer, you agree to buy this item for{' '}
          <span className="font-bold text-black">
            {formatCurrency(offer.total)}
          </span>
        </h3>

        <div className="mt-4 flex w-full justify-center">
          <div className="flex items-center justify-center rounded-2xl bg-brand-lightest-gray px-4 py-2 text-[1.8rem] text-zinc-900">
            ({formatCurrency(offer.price)} +{' '}
            {formatCurrency(offer.shipping_cost)} Shipping +{' '}
            {formatCurrency(offer.tax)} Tax)
          </div>
        </div>
      </div>

      {isLoading && !offer.payment?.id ? (
        <div className="relative h-[14rem] w-full p-8">
          <div className="h-[10rem] w-full animate-pulse rounded-2xl bg-brand-lightest-gray" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[1.8rem] font-medium text-zinc-600">
              Loading Payment Methods...
            </span>
          </div>
        </div>
      ) : (
        <>
          {offer.is_exclusive && (
            <div className="space-y-8 p-8">
              <FormLabel
                value="Shipping Address"
                errorMessage={errors.shipping_address?.message}
              >
                <Controller
                  name="shipping_address"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      disabled={!addressOptions.length}
                      placeholder="Select Shipping Address"
                      options={addressOptions}
                      error={!!errors.shipping_address}
                      control={control}
                      allowEmpty={false}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (!product?.is_flat_rate) {
                          setSelectedRate(null);
                        }
                      }}
                    />
                  )}
                />
              </FormLabel>

              <FormLabel
                value="Payment Method"
                required
                errorMessage={errors.payment_method?.message}
              >
                {selectedPayment && (
                  <div className="mb-4">
                    <CreditCard
                      key={`selected-${selectedPayment.id}`}
                      label={`${capitalize(selectedPayment.brand)} - ${
                        selectedPayment.last4
                      }`}
                      name={selectedPayment.name ?? ''}
                      selected={true}
                      showArrow
                      onClickArrow={() => {
                        setCardListCollapsed(!cardListCollapsed);
                      }}
                      arrowDirection={cardListCollapsed ? 'up' : 'down'}
                    />
                  </div>
                )}

                {cardListCollapsed && (
                  <Controller
                    name="payment_method"
                    control={control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-y-4">
                        {paymentMethodData
                          ?.filter(
                            (option) => option.id !== selectedPayment?.id
                          )
                          .map((option) => (
                            <CreditCard
                              key={option.id}
                              label={`${capitalize(option.brand)} - ${
                                option.last4
                              }`}
                              name={option.name ?? ''}
                              selected={false}
                              onClick={() => {
                                field.onChange(option.id);
                                if (cardListCollapsed) {
                                  setCardListCollapsed(false);
                                }
                              }}
                            />
                          ))}
                      </div>
                    )}
                  />
                )}
              </FormLabel>

              {form.shipping_address && product && !product.is_flat_rate && (
                <OfferShippingOptions
                  product={product}
                  shipTo={userDoc?.addresses.find(
                    (a) =>
                      `${a.address_line1} - ${a.city_locality}, ${a.state_province}, ${a.postal_code}` ===
                      form.shipping_address
                  )}
                  offer_amount={offer.price}
                  selectedRate={selectedRate}
                  setSelectedRate={setSelectedRate}
                  ratesError={ratesError}
                  setRatesError={setRatesError}
                />
              )}
            </div>
          )}
        </>
      )}

      {taxError && (
        <div className="flex w-full flex-col items-center justify-center bg-brand-lightest-gray p-12">
          <span className="text-[1.8rem] text-zinc-700">
            {taxError.message}
          </span>
        </div>
      )}

      <div className="flex w-full flex-col items-center justify-center bg-brand-lightest-gray p-12">
        <span className="text-[1.8rem] text-zinc-700">
          Once accepted, offers are binding and cannot be cancelled.
        </span>

        <div className="mt-8 flex w-full items-center gap-x-8">
          <button
            type="button"
            className="flex w-full items-center justify-center rounded-[1.5rem] border border-brand-black bg-inherit p-4 text-[1.8rem] font-semibold text-brand-black"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={
              loadingSubmit ||
              calculatingTax ||
              !form.shipping_address ||
              !form.payment_method ||
              !!taxError
            }
            className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#1E7A1E] p-4 text-[1.8rem] font-semibold text-white disabled:bg-brand-gray disabled:text-black"
          >
            {loadingSubmit
              ? 'Accepting...'
              : calculatingTax
              ? 'Calculating tax...'
              : 'Accept'}
          </button>
        </div>
        {errors?.root && (
          <p className="mt-[1.3rem] text-red-500">{errors.root.message}</p>
        )}
      </div>
    </form>
  );
};
